/* eslint-disable max-len */
import { Button, Col, Row, Space } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipleDealerIds } from '../../../../components/contexts/DealerContextManager';
import DealershipTranslatedTextAreaField from '../../../../components/fields/DealershipFields/DealershipTranslatedTextArea';
import DealershipTranslationTextField from '../../../../components/fields/DealershipFields/DealershipTranslationTextField';
import SingleUploadField from '../../../../components/fields/SingleUploadField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../themes/admin/Fields/FormFields';
import { allowedExtensions } from '../../../../utilities/extensions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';

type ConfiguratorModuleEmailFormProps = {
    prefix?: string;
    showUpdateButton?: boolean;
    targetDealerId?: string;
    companyId?: string;
    defaultValueDisabled: boolean;
    disabled?: boolean;
};

const fieldNames = [
    'saveOrder.subject',
    'saveOrder.introTitle',
    'saveOrder.contentText',
    'saveOrder.bannerImage',
    'saveOrder.introImage',
    'submitOrder.subject',
    'submitOrder.introTitle',
    'submitOrder.contentText',
    'submitOrder.isPaymentSectionShown',
    'submitOrder.introImage',
] as const;

const ConfiguratorModuleEmailForm = ({
    prefix,
    showUpdateButton = true,
    targetDealerId,
    companyId,
    defaultValueDisabled,
    disabled,
}: ConfiguratorModuleEmailFormProps) => {
    const { t } = useTranslation('configuratorModuleDetail');

    const { yesNoSwitch } = useSystemSwitchData();

    const { dealerIdsByCompanyId } = useMultipleDealerIds();

    const dealerIds = useMemo(
        () => (targetDealerId ? [targetDealerId] : dealerIdsByCompanyId(companyId)),
        [companyId, dealerIdsByCompanyId, targetDealerId]
    );

    const names = useMemo(
        () =>
            Object.fromEntries(fieldNames.map(name => [name, prefix ? `${prefix}.${name}` : name])) as Record<
                (typeof fieldNames)[number],
                string
            >,
        [prefix]
    );

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <CollapsibleWrapper defaultActiveKey={t('configuratorModuleDetail:subTitles.emails.saveOrder')}>
                <Panel
                    key={t('configuratorModuleDetail:subTitles.emails.saveOrder')}
                    extra={
                        showUpdateButton &&
                        !disabled && (
                            <Button form="updateEmailForm" htmlType="submit" type="primary">
                                {t('configuratorModuleDetail:mainActionButtons.update')}
                            </Button>
                        )
                    }
                    header={t('configuratorModuleDetail:subTitles.emails.saveOrder')}
                >
                    <Row gutter={10}>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('configuratorModuleDetail:fields.saveOrder.subject', { returnObjects: true })}
                                contentType="email"
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['saveOrder.subject']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('configuratorModuleDetail:fields.saveOrder.introTitle', { returnObjects: true })}
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['saveOrder.introTitle']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslatedTextAreaField
                                {...t('configuratorModuleDetail:fields.saveOrder.contentText', { returnObjects: true })}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['saveOrder.contentText']}
                                targetDealerId={targetDealerId}
                                withContentRefinement
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <SingleUploadField
                                disabled={disabled}
                                extensions={allowedExtensions.image}
                                {...t('configuratorModuleDetail:fields.saveOrder.bannerImage', { returnObjects: true })}
                                name={names['saveOrder.bannerImage']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <SingleUploadField
                                disabled={disabled}
                                extensions={allowedExtensions.image}
                                {...t('configuratorModuleDetail:fields.saveOrder.introImage', { returnObjects: true })}
                                name={names['saveOrder.introImage']}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>

            <CollapsibleWrapper defaultActiveKey={t('configuratorModuleDetail:subTitles.emails.acknowledgementEmail')}>
                <Panel
                    key={t('configuratorModuleDetail:subTitles.emails.acknowledgementEmail')}
                    extra={
                        showUpdateButton &&
                        !disabled && (
                            <Button form="updateEmailForm" htmlType="submit" type="primary">
                                {t('configuratorModuleDetail:mainActionButtons.update')}
                            </Button>
                        )
                    }
                    header={t('configuratorModuleDetail:subTitles.emails.acknowledgementEmail')}
                >
                    <Row gutter={10}>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('configuratorModuleDetail:fields.submitOrder.subject', { returnObjects: true })}
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.subject']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslationTextField
                                {...t('configuratorModuleDetail:fields.submitOrder.introTitle', {
                                    returnObjects: true,
                                })}
                                dealerIds={dealerIds}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.introTitle']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <DealershipTranslatedTextAreaField
                                {...t('configuratorModuleDetail:fields.submitOrder.contentText', {
                                    returnObjects: true,
                                })}
                                defaultValueDisabled={defaultValueDisabled}
                                disabled={disabled}
                                name={names['submitOrder.contentText']}
                                targetDealerId={targetDealerId}
                                withContentRefinement
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <FormFields.SwitchField
                                {...yesNoSwitch}
                                {...t('configuratorModuleDetail:fields.submitOrder.isPaymentSectionShown', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={names['submitOrder.isPaymentSectionShown']}
                            />
                        </Col>
                        <Col lg={8} xs={24}>
                            <SingleUploadField
                                extensions={allowedExtensions.image}
                                {...t('configuratorModuleDetail:fields.submitOrder.introImage', {
                                    returnObjects: true,
                                })}
                                disabled={disabled}
                                name={names['submitOrder.introImage']}
                            />
                        </Col>
                    </Row>
                </Panel>
            </CollapsibleWrapper>
        </Space>
    );
};

export default ConfiguratorModuleEmailForm;
